<script setup lang="ts">
import type { IRadioInputItem } from "@/components/ui/RadioInput/radioInput.interface"
import type { IReminder } from "@/types"
import { toTypedSchema } from "@vee-validate/zod"
import { useForm } from "vee-validate"
import { computed, ref, watch } from "vue"
import { z } from "zod"
import { useCreateReminder } from "@/api"
import { Alert, BaseModal, Button, Card, Icon, Input, RadioInput } from "@/components/ui"
import { useToast } from "@/components/ui/ToastReka/useToast"
import { useModal, useTranslation } from "@/composables"
import { ValidationException } from "@/http"
import { GuestReminderModalKey } from "@/symbols"
import { ReminderOptions } from "@/types"

interface GuestReminderModalProps {
  eventVaultId: number
}

const props = defineProps<GuestReminderModalProps>()

const { t } = useTranslation()

const toast = useToast()

const eventVaultId = computed(() => props.eventVaultId)

const { toggle, isActive } = useModal(GuestReminderModalKey)

const alertMessage = ref<string>(t("remind_me_later_page.alert"))
const validationErrors = ref()

const whenItems: IRadioInputItem[] = [
  { value: ReminderOptions.Now, label: t("remind_me_later_page.form.now") },
  { value: ReminderOptions.OneDay, label: t("remind_me_later_page.form.one_day") },
  { value: ReminderOptions.OneWeek, label: t("remind_me_later_page.form.one_week") },
]

const validationSchema = toTypedSchema(
  z.object({
    name: z.string().optional(),
    email: z.string().email(t("remind_me_later_page.error.email")),
    when: z.nativeEnum(ReminderOptions),
  }),
)

const { handleSubmit, handleReset } = useForm({
  validationSchema,
  initialValues: {
    name: "",
    email: "",
    when: ReminderOptions.Now,
  },
})

const { mutate, isPending } = useCreateReminder(eventVaultId)

const onSubmit = handleSubmit(({ name, email, when }) => {
  validationErrors.value = undefined

  const reminder: IReminder = { name, email, when }

  mutate(reminder, {
    onSuccess: (message: string) => {
      handleReset()
      validationErrors.value = undefined
      toggle()
      toast.success({ description: message })
    },
    onError: (error) => {
      let errorMessage = t("try_again")
      if (error instanceof ValidationException) {
        errorMessage = t("validation_error")
        validationErrors.value = error
      }
      toast.error({ description: errorMessage })
    },
  })
})

watch(isActive, (active) => {
  if (!active) {
    handleReset()
    validationErrors.value = undefined
  }
})
</script>

<template>
  <BaseModal
    :name="GuestReminderModalKey"
    transition="slide-up"
    :close-on-backdrop-click="true"
  >
    <div class="relative z-10 mx-auto max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white shadow-xl guest-layout">
      <Button
        shape="circle"
        variant="secondary"
        size="sm"
        class="absolute right-2 top-2 z-10"
        @click="toggle"
      >
        <Icon icon="Close" />
      </Button>

      <Card variant="secondary" :with-padding="false" class="overflow-hidden rounded-xl border-0 shadow-none text-secondary-card-text">
        <div class="p-5 font-montserrat md:p-6">
          <h2 class="pr-10 font-comfortaa text-xl font-semibold md:text-2xl">
            {{ t("guest_reminder_modal.title") }}
          </h2>
          <p class="mt-2 text-sm leading-relaxed">
            {{ t("guest_reminder_modal.description") }}
          </p>
          <div class="mt-4 rounded-md bg-white">
            <Alert v-model="alertMessage" prefix-icon="CircleInfo" :closable="false" />
          </div>

          <Transition name="gpx-fade" mode="out-in">
            <div v-if="isPending" class="mt-6 flex flex-col gap-4">
              <div class="gpx-skeleton h-14 w-full rounded-lg" />
              <div class="gpx-skeleton h-14 w-full rounded-lg" />
              <div class="gpx-skeleton h-14 w-full rounded-lg" />
              <div class="gpx-skeleton h-11 w-32 rounded-lg" />
            </div>
            <form v-else class="mt-6 flex flex-col gap-4" @submit.prevent="onSubmit">
              <Input name="name" :label="t('remind_me_later_page.form.name')" />
              <Input
                name="email"
                :label="t('remind_me_later_page.form.email')"
                :error="validationErrors?.firstValidationError('Email')"
              />
              <RadioInput
                name="when"
                :label="t('remind_me_later_page.form.date')"
                :items="whenItems"
                :error="validationErrors?.firstValidationError('RemindMeWhen')"
              />
              <div class="flex">
                <Button class="w-full" size="lg" data-test="remind-me-submit" type="submit">
                  {{ t("submit") }}
                </Button>
              </div>
            </form>
          </Transition>
        </div>
      </Card>
    </div>
  </BaseModal>
</template>
